import React, { useMemo, useState } from "react";
import { CalendarClock, IndianRupee, Send } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppDataTable, Callout, FilterToolbar, ProgressBar, StatusBadge,
} from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import FeePlanModal from "../../../Fees/FeePlanModal";
import { DASH, formatCurrency, formatDate } from "../../../../utils/format";
import RecordCardHead, { TableHeading } from "../RecordCardHead";
import RecordStat from "../RecordStat";
import PaymentHistory from "./PaymentHistory";
import { installmentBalance, installmentStatus, istDayKey } from "../studentRecord";
import { describeDiscount } from "../../../../utils/enrolmentFee";

const idOf = (ref) => String((ref && typeof ref === "object" ? ref._id : ref) || "");

/** The plan that schedules an enrolment — by assignment, or by course for a legacy primary. */
const planFor = (enrollment, plans) => plans.find((p) => (
  enrollment.courseAssignment
    ? idOf(p.courseAssignmentId) === String(enrollment.courseAssignment._id)
    : !p.courseAssignmentId && idOf(p.courseId) === String(enrollment.courseId)
));

const REMINDABLE = new Set(["due", "overdue", "partial"]);

/** One enrolment's fee position, and its installment schedule when it has one. */
const EnrollmentFees = ({ enrollment: e, plan, todayKey, canManage, onEditPlan, onRemind, reminding }) => {
  const title = `${e.courseName}${e.batch?.name ? ` · ${e.batch.name}` : ""}`;
  const discount = describeDiscount(e.discount);
  const position = `${formatCurrency(e.paidFees)} of ${formatCurrency(e.totalFees)} paid${e.pendingFees > 0 ? ` · ${formatCurrency(e.pendingFees)} due` : ""}${discount ? ` · ${discount}` : ""}`;
  // A legacy primary has no assignment row, and a plan hangs off one.
  const planAction = canManage && e.courseAssignment && (
    <AppButton size="sm" variant="outline" onClick={() => onEditPlan(e, plan)}>
      <CalendarClock size={16} /> {plan ? "Edit plan" : "Set up plan"}
    </AppButton>
  );

  if (!plan) {
    return (
      <AppCard>
        <RecordCardHead
          title={title}
          description={`${position} · paid without an installment plan`}
          action={planAction}
        />
        <AppCardContent>
          <div className="tw-flex tw-items-center tw-gap-3">
            <ProgressBar
              value={e.totalFees > 0 ? (e.paidFees / e.totalFees) * 100 : 0}
              color={e.pendingFees > 0 ? "warning" : "success"}
              size="md"
            />
            {e.status !== "active" && <StatusBadge status={e.status} domain="enrollment" />}
          </div>
        </AppCardContent>
      </AppCard>
    );
  }

  const rows = [...(plan.installments || [])].sort((a, b) => a.seq - b.seq);
  const columns = [
    {
      key: "label",
      label: "Installment",
      render: (i) => <span className="tw-font-medium tw-text-content">{i.label || `EMI ${i.seq}`}</span>,
    },
    { key: "due", label: "Due", render: (i) => <span className="tw-tabular-nums">{formatDate(i.dueDate)}</span> },
    { key: "amount", label: "Amount", align: "right", render: (i) => <span className="tw-tabular-nums">{formatCurrency(i.amount)}</span> },
    {
      key: "paid",
      label: "Paid",
      align: "right",
      render: (i) => <span className="tw-tabular-nums tw-text-content-muted">{i.paidAmount ? formatCurrency(i.paidAmount) : DASH}</span>,
    },
    {
      key: "balance",
      label: "Balance",
      align: "right",
      render: (i) => {
        const balance = installmentBalance(i);
        return <span className="tw-font-medium tw-tabular-nums">{balance ? formatCurrency(balance) : DASH}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      render: (i) => <StatusBadge status={installmentStatus(i, todayKey)} domain="feeInstallment" />,
    },
    ...(onRemind ? [{
      key: "remind",
      label: "",
      align: "right",
      resizable: false,
      render: (i) => (REMINDABLE.has(installmentStatus(i, todayKey)) && installmentBalance(i) > 0 ? (
        <AppButton
          size="xs"
          variant="ghost"
          onClick={() => onRemind({ planId: plan._id, seq: i.seq, label: i.label || `EMI ${i.seq}` })}
          disabled={reminding === `${plan._id}:${i.seq}`}
        >
          <Send size={14} /> {reminding === `${plan._id}:${i.seq}` ? "Sending…" : "Remind"}
        </AppButton>
      ) : null),
    }] : []),
  ];

  // A plan's installments must add up to the fee, and a later fee or discount
  // change does not rewrite the plan — ₹1 is the drift the plan check allows.
  // What the installments add up to is what the plan check compares.
  const planTotal = (plan.installments || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const planOff = Math.abs(planTotal - e.totalFees) > 1;

  const table = (
    <AppDataTable
      fill={false}
      toolbar={(
        <FilterToolbar flush actions={planAction}>
          <TableHeading title={title} count={position} />
          {e.status !== "active" && <StatusBadge status={e.status} domain="enrollment" />}
        </FilterToolbar>
      )}
      columns={columns}
      rows={rows}
      rowKey={(i) => `${plan._id}:${i.seq}`}
      emptyMessage="This plan has no installments."
    />
  );
  if (!planOff) return table;
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <Callout tone="warning">
        This plan's installments total {formatCurrency(planTotal)}, but the fee for this course is now {formatCurrency(e.totalFees)}.
        {canManage ? " Edit the plan so they add up to the fee." : " Ask an admin to update the plan."}
      </Callout>
      {table}
    </div>
  );
};

/**
 * FeesTab — what the student owes and has paid, course by course, with the
 * installment schedule and every payment on record.
 *
 * Props:
 *   student, enrollments, fees, installments — from studentRecord
 *   plans, payments — the page's resources
 *   canCollect      — the viewer may collect fees and manage plans
 *   onCollect       — to the collect-fee screen
 *   onRemind        — ({ planId, seq, label }) sends an installment reminder; omitted when the viewer may not
 *   reminding       — "planId:seq" of the reminder in flight
 *   onChanged       — a plan was saved or a payment refunded: reload the money
 *   onNotify        — (message, type) for the page's toast
 */
const FeesTab = ({
  student, enrollments, fees, installments, plans, payments,
  canCollect, onCollect, onRemind, reminding, onChanged, onNotify,
}) => {
  const [planTarget, setPlanTarget] = useState(null);
  const todayKey = istDayKey(new Date());

  // Every enrolment that carries money, or a plan to collect it by.
  const billed = enrollments.filter((e) => e.totalFees > 0 || e.paidFees > 0 || planFor(e, plans.data || []));
  const courseNames = useMemo(() => Object.fromEntries(
    enrollments.filter((e) => e.courseAssignment).map((e) => [String(e.courseAssignment._id), e.courseName]),
  ), [enrollments]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <RecordCardHead
          title="Fee summary"
          description="Across active and completed courses"
          action={canCollect && (
            <AppButton size="sm" variant="outline" onClick={onCollect}>
              <IndianRupee size={16} /> Collect fee
            </AppButton>
          )}
        />
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 md:tw-grid-cols-5">
            <RecordStat
              label="Total fees"
              value={formatCurrency(fees.total)}
              hint={fees.discount > 0 ? `after ${formatCurrency(fees.discount)} discount` : undefined}
            />
            <RecordStat label="Paid" value={formatCurrency(fees.paid)} hint={fees.paidPct !== null ? `${fees.paidPct}% of total` : undefined} />
            <RecordStat label="Balance" value={formatCurrency(fees.pending)} tone={fees.pending > 0 ? "warning" : "success"} />
            <RecordStat
              label="Overdue"
              value={installments?.overdueCount ? formatCurrency(installments.overdueAmount) : DASH}
              tone={installments?.overdueCount ? "danger" : undefined}
              hint={installments?.overdueCount ? `${installments.overdueCount} installment${installments.overdueCount > 1 ? "s" : ""}` : "Nothing overdue"}
            />
            <RecordStat
              label="Next installment"
              value={installments?.next ? formatCurrency(installments.next.balance) : DASH}
              hint={installments?.next ? `due ${formatDate(installments.next.dueDate)}` : "None scheduled"}
            />
          </dl>
          {fees.total > 0 && <ProgressBar value={fees.paidPct || 0} color="success" size="md" />}
        </AppCardContent>
      </AppCard>

      {plans.loading && !plans.data ? (
        <Shimmer height={120} />
      ) : (
        billed.map((e) => (
          <EnrollmentFees
            key={e.key}
            enrollment={e}
            plan={planFor(e, plans.data || [])}
            todayKey={todayKey}
            canManage={canCollect}
            onEditPlan={(enrollment, plan) => setPlanTarget({ enrollment, plan })}
            onRemind={onRemind}
            reminding={reminding}
          />
        ))
      )}

      <PaymentHistory
        studentId={student._id}
        studentName={student.name}
        payments={payments}
        courseNames={courseNames}
        onCollect={canCollect ? onCollect : null}
        onChanged={onChanged}
        onNotify={onNotify}
      />

      <FeePlanModal
        show={!!planTarget}
        onHide={() => setPlanTarget(null)}
        courseAssignment={planTarget?.enrollment.courseAssignment}
        existingPlan={planTarget?.plan || null}
        onSaved={() => { setPlanTarget(null); onNotify("Installment plan saved.", "success"); onChanged(); }}
      />
    </div>
  );
};

export default FeesTab;
